import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  CircularProgress,
  Chip,
  Box,
  Badge,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Drawer,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FilterAltOff, FilterAlt, Close as CloseIcon } from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import dayjs from "dayjs";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import api from "services/api";
import xmlTypeLabel from "common/utils/xmlTypeLabel";

const FilterBar = ({ filters, setFilters, documentCount, hideClassificationFilters, children }) => {
  const { selectedCompany } = useCompany();
  const { t } = useLanguage();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  // Local states for filter values
  const [localDateType, setLocalDateType] = useState(filters.dateType || "invoice");
  const [localStartDate, setLocalStartDate] = useState(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [localEndDate, setLocalEndDate] = useState(filters.endDate ? dayjs(filters.endDate) : null);
  const [localSupplierSearchText, setLocalSupplierSearchText] = useState("");
  const [localSupplierOptions, setLocalSupplierOptions] = useState([]);
  const [localSupplierLoading, setLocalSupplierLoading] = useState(false);
  const [localSelectedSuppliers, setLocalSelectedSuppliers] = useState(filters.suppliers || []);
  const [localXmlType, setLocalXmlType] = useState(filters.xmlType || "");
  const [localExcludeSuppliers, setLocalExcludeSuppliers] = useState(
    filters.excludeSuppliers || false
  );
  const [localDocumentTypes, setLocalDocumentTypes] = useState(filters.documentTypes || []);
  const [localExcludeDocumentTypes, setLocalExcludeDocumentTypes] = useState(
    filters.excludeDocumentTypes || false
  );
  const [localRecognitionStatus, setLocalRecognitionStatus] = useState(
    filters.recognitionStatus || ""
  );
  const [localAdvanceReport, setLocalAdvanceReport] = useState(filters.advanceReport || "");

  const translateWithFallback = (key, fallback) => {
    const translated = t ? t(key) : null;
    return translated && translated !== key ? translated : fallback;
  };

  const documentTypeOptions = useMemo(() => {
    return [
      {
        value: "purchase",
        label: translateWithFallback("purchaseDocument", "Pirkimo dokumentas"),
      },
      {
        value: "sale",
        label: translateWithFallback("salesDocument", "Pardavimo dokumentas"),
      },
      {
        value: "return",
        label: translateWithFallback("returnDocument", "Grąžinimo dokumentas"),
      },
      {
        value: "other",
        label: translateWithFallback("otherDocument", "Kitas dokumentas"),
      },
    ];
  }, [t]);

  const documentTypeLabelMap = useMemo(() => {
    return documentTypeOptions.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [documentTypeOptions]);

  const toggleFilterDisplay = () => {
    setIsFilterOpen((prev) => !prev);
  };

  useEffect(() => {
    const fetchSuppliers = async () => {
      if (!selectedCompany?.id) return;
      if (localSupplierSearchText.length >= 2) {
        setLocalSupplierLoading(true);
        try {
          // CSRF token (if required by backend)
          await api.get("/api/get-csrf-token/");

          const response = await api.get(
            `/api/get-supplier-variants/?search_term=${localSupplierSearchText}&company_id=${selectedCompany.id}`
          );

          if (response.data && response.data.suppliers) {
            setLocalSupplierOptions(response.data.suppliers);
          } else {
            setLocalSupplierOptions([]);
          }
        } catch (error) {
          console.error("Error fetching suppliers:", error);
          setLocalSupplierOptions([]);
        } finally {
          setLocalSupplierLoading(false);
        }
      } else {
        setLocalSupplierOptions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuppliers, 300);
    return () => clearTimeout(debounceTimer);
  }, [localSupplierSearchText, selectedCompany?.id]);

  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      dateType: localDateType,
      startDate: localStartDate ? localStartDate.toISOString() : null,
      endDate: localEndDate ? localEndDate.toISOString() : null,
      suppliers: localSelectedSuppliers,
      excludeSuppliers: localExcludeSuppliers,
      xmlType: localXmlType,
      documentTypes: hideClassificationFilters ? [] : localDocumentTypes,
      excludeDocumentTypes: hideClassificationFilters ? false : localExcludeDocumentTypes,
      recognitionStatus: hideClassificationFilters ? "" : localRecognitionStatus,
      advanceReport: localAdvanceReport,
    });
    setIsFilterOpen(false);
  };

  const handleResetFilters = () => {
    setLocalDateType("invoice");
    setLocalStartDate(null);
    setLocalEndDate(null);
    setLocalSupplierSearchText("");
    setLocalSelectedSuppliers([]);
    setLocalXmlType("");
    setLocalExcludeSuppliers(false);
    setLocalDocumentTypes([]);
    setLocalExcludeDocumentTypes(false);
    setLocalRecognitionStatus("");
    setLocalAdvanceReport("");
    setFilters({
      suppliers: [],
      dateType: "invoice",
      startDate: null,
      endDate: null,
      xmlType: "",
      excludeSuppliers: false,
      documentTypes: [],
      excludeDocumentTypes: false,
      recognitionStatus: "",
      advanceReport: "",
      searchQuery: filters.searchQuery,
    });
    setIsFilterOpen(false);
  };

  const handleSupplierChange = (event, newValue) => {
    setLocalSelectedSuppliers(newValue);
  };

  const handleDateTypeChange = (event) => {
    setLocalDateType(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setLocalStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setLocalEndDate(date);
  };

  // Keep local UI state in sync when parent filters change (e.g., after navigation back)
  useEffect(() => {
    setLocalDateType(filters.dateType || "invoice");
    setLocalStartDate(filters.startDate ? dayjs(filters.startDate) : null);
    setLocalEndDate(filters.endDate ? dayjs(filters.endDate) : null);
    setLocalSelectedSuppliers(filters.suppliers || []);
    setLocalXmlType(filters.xmlType || "");
    setLocalExcludeSuppliers(!!filters.excludeSuppliers);
    setLocalDocumentTypes(filters.documentTypes || []);
    setLocalExcludeDocumentTypes(!!filters.excludeDocumentTypes);
    setLocalRecognitionStatus(filters.recognitionStatus || "");
    setLocalAdvanceReport(filters.advanceReport || "");
  }, [filters]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.suppliers && filters.suppliers.length > 0) count++;
    if (filters.startDate || filters.endDate) count++;
    if (!hideClassificationFilters && filters.documentTypes && filters.documentTypes.length > 0)
      count++;
    if (filters.xmlType) count++;
    if (!hideClassificationFilters && filters.recognitionStatus) count++;
    if (filters.advanceReport) count++;
    return count;
  }, [filters, hideClassificationFilters]);

  const removeFilter = (filterKey) => {
    const newFilters = { ...filters };
    switch (filterKey) {
      case "suppliers":
        newFilters.suppliers = [];
        newFilters.excludeSuppliers = false;
        break;
      case "dates":
        newFilters.startDate = null;
        newFilters.endDate = null;
        break;
      case "documentTypes":
        newFilters.documentTypes = [];
        newFilters.excludeDocumentTypes = false;
        break;
      case "xmlType":
        newFilters.xmlType = "";
        break;
      case "recognitionStatus":
        newFilters.recognitionStatus = "";
        break;
      case "advanceReport":
        newFilters.advanceReport = "";
        break;
      default:
        break;
    }
    setFilters(newFilters);
  };

  const filterButtonNode = (
    <Badge
      badgeContent={activeFiltersCount > 0 ? activeFiltersCount : null}
      color="primary"
      sx={{
        "& .MuiBadge-badge": {
          fontSize: "0.75rem",
          height: "22px",
          minWidth: "22px",
          padding: "0 6px",
        },
      }}
    >
      <MDButton
        variant="contained"
        color="info"
        onClick={toggleFilterDisplay}
        startIcon={<FilterAlt />}
        sx={{
          minWidth: 110,
          height: "40px",
          boxShadow: 1,
          "&:hover": { boxShadow: 2 },
          whiteSpace: "nowrap",
        }}
      >
        {t("filters")}
      </MDButton>
    </Badge>
  );

  const activeChipsNode = (
    <>
      {filters.suppliers?.length > 0 && (
        <Chip
          label={`${t("suppliers")}: ${filters.suppliers.length} ${
            filters.excludeSuppliers ? `(${t("excluded")})` : ""
          }`}
          onDelete={() => removeFilter("suppliers")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {(filters.startDate || filters.endDate) && (
        <Chip
          label={`${t("dates")}: ${
            filters.startDate ? dayjs(filters.startDate).format("YYYY-MM-DD") : "..."
          } - ${filters.endDate ? dayjs(filters.endDate).format("YYYY-MM-DD") : "..."}`}
          onDelete={() => removeFilter("dates")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {!hideClassificationFilters && filters.documentTypes?.length > 0 && (
        <Chip
          label={`${t("documentType")}: ${filters.documentTypes.length} ${
            filters.excludeDocumentTypes ? `(${t("excluded")})` : ""
          }`}
          onDelete={() => removeFilter("documentTypes")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {filters.xmlType && (
        <Chip
          label={`${t("xmlType")}: ${xmlTypeLabel(filters.xmlType, t)}`}
          onDelete={() => removeFilter("xmlType")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {!hideClassificationFilters && filters.recognitionStatus && (
        <Chip
          label={`${t("recognitionStatus") || "Atpažinimo būsena"}: ${
            filters.recognitionStatus === "recognized"
              ? t("recognized") || "Atpažinti"
              : t("unrecognized") || "Neatpažinti"
          }`}
          onDelete={() => removeFilter("recognitionStatus")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {filters.advanceReport && (
        <Chip
          label={`${translateWithFallback("advanceReport", "Avansinė apyskaita")}: ${
            filters.advanceReport === "yes"
              ? translateWithFallback("yes", "Taip")
              : translateWithFallback("no", "Ne")
          }`}
          onDelete={() => removeFilter("advanceReport")}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      {activeFiltersCount > 0 && (
        <Chip
          label={t("resetFilters") || "Atstatyti filtrus"}
          onClick={handleResetFilters}
          color="secondary"
          variant="outlined"
          size="small"
          sx={{ cursor: "pointer" }}
        />
      )}
    </>
  );

  const drawerNode = (
    <Drawer
      anchor="right"
      open={isFilterOpen}
      onClose={() => setIsFilterOpen(false)}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 400 }, p: 3 },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("filters")}</Typography>
        <IconButton onClick={() => setIsFilterOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Box display="flex" flexDirection="column" gap={3}>
        <Box>
          <Autocomplete
            multiple
            options={localSupplierOptions}
            value={localSelectedSuppliers}
            onChange={handleSupplierChange}
            onInputChange={(event, value) => setLocalSupplierSearchText(value)}
            getOptionLabel={(option) => option.supplier_name || ""}
            fullWidth
            size="small"
            limitTags={2}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("suppliers")}
                placeholder={t("searchSuppliers")}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {localSupplierLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.supplier_name}
                  {...getTagProps({ index })}
                  size="small"
                  key={option.supplier_registration || `${option.supplier_name}-${index}`}
                />
              ))
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => setLocalExcludeSuppliers(e.target.checked)}
                color="primary"
                checked={localExcludeSuppliers}
              />
            }
            label={t("excludeSuppliers")}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box>
          <TextField
            size="small"
            select
            label={t("dateType")}
            value={localDateType}
            onChange={handleDateTypeChange}
            SelectProps={{ native: true }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <option value="invoice">{t("invoiceDate")}</option>
            <option value="upload">{t("uploadDate")}</option>
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2}>
              <DatePicker
                label={t("startDate")}
                value={localStartDate}
                onChange={handleStartDateChange}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <DatePicker
                label={t("endDate")}
                value={localEndDate}
                onChange={handleEndDateChange}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {!hideClassificationFilters && (
          <Box>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{t("documentType")}</InputLabel>
              <Select
                multiple
                value={localDocumentTypes}
                onChange={(event) => {
                  const { value } = event.target;
                  setLocalDocumentTypes(typeof value === "string" ? value.split(",") : value);
                }}
                label={t("documentType")}
                renderValue={(selected) =>
                  selected.length > 0
                    ? selected.map((type) => documentTypeLabelMap[type] || type).join(", ")
                    : translateWithFallback(
                        "allDocumentTypes",
                        translateWithFallback("all", "Visi dokumentų tipai")
                      )
                }
              >
                {documentTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={localDocumentTypes.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => setLocalExcludeDocumentTypes(e.target.checked)}
                  color="primary"
                  checked={localExcludeDocumentTypes}
                />
              }
              label={translateWithFallback("excludeDocumentTypes", "Išskirti pasirinktus tipus")}
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>{t("xmlType")}</InputLabel>
          <Select
            value={localXmlType}
            onChange={(e) => setLocalXmlType(e.target.value)}
            label={t("xmlType")}
          >
            <MenuItem value="">{t("all")}</MenuItem>
            <MenuItem value="quantitative">{t("quantitative")}</MenuItem>
            <MenuItem value="summary">{t("summary")}</MenuItem>
          </Select>
        </FormControl>

        {!hideClassificationFilters && (
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>{t("recognitionStatus") || "Atpažinimo būsena"}</InputLabel>
            <Select
              value={localRecognitionStatus}
              onChange={(e) => setLocalRecognitionStatus(e.target.value)}
              label={t("recognitionStatus") || "Atpažinimo būsena"}
            >
              <MenuItem value="">{t("all") || "Visi"}</MenuItem>
              <MenuItem value="unrecognized">{t("unrecognized") || "Neatpažinti"}</MenuItem>
              <MenuItem value="recognized">{t("recognized") || "Atpažinti"}</MenuItem>
            </Select>
          </FormControl>
        )}

        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>{translateWithFallback("advanceReport", "Avansinė apyskaita")}</InputLabel>
          <Select
            value={localAdvanceReport}
            onChange={(e) => setLocalAdvanceReport(e.target.value)}
            label={translateWithFallback("advanceReport", "Avansinė apyskaita")}
          >
            <MenuItem value="">{t("all") || "Visi"}</MenuItem>
            <MenuItem value="yes">
              {translateWithFallback("advanceReportOnly", "Tik avansinės")}
            </MenuItem>
            <MenuItem value="no">
              {translateWithFallback("notAdvanceReport", "Ne avansinės")}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2} mt="auto" pt={4}>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={handleResetFilters}
          startIcon={<FilterAltOff />}
          fullWidth
        >
          {t("resetFilters")}
        </MDButton>
        <MDButton
          variant="contained"
          color="info"
          onClick={handleApplyFilters}
          startIcon={<FilterAlt />}
          fullWidth
        >
          {t("apply")}
        </MDButton>
      </Box>
    </Drawer>
  );

  if (typeof children === "function") {
    return (
      <>
        {children({
          filterButton: filterButtonNode,
          activeChips: activeChipsNode,
          activeFiltersCount,
          openFilters: () => setIsFilterOpen(true),
          closeFilters: () => setIsFilterOpen(false),
          resetFilters: handleResetFilters,
        })}
        {drawerNode}
      </>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {filterButtonNode}
        <Box display="flex" gap={1} flexWrap="wrap">
          {activeChipsNode}
        </Box>
      </Box>
      {drawerNode}
    </Box>
  );
};

FilterBar.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  filters: PropTypes.shape({
    suppliers: PropTypes.array,
    excludeSuppliers: PropTypes.bool,
    documentTypes: PropTypes.array,
    excludeDocumentTypes: PropTypes.bool,
    dateType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    xmlType: PropTypes.string,
    searchQuery: PropTypes.string,
    recognitionStatus: PropTypes.string,
    advanceReport: PropTypes.string,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  documentCount: PropTypes.number,
  hideClassificationFilters: PropTypes.bool,
};

FilterBar.defaultProps = {
  documentCount: 0,
  hideClassificationFilters: false,
  children: null,
};

export default FilterBar;
